import { t as tCem } from '../utils/i18n'
import { useState, useEffect, useCallback } from 'react'
import {
  AlertTriangle, WifiOff, Camera, Car, MapPin,
  ChevronRight, CheckCircle2, Clock,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import SlideDrawer from '../components/SlideDrawer'

// ─── Paleta de severidades ────────────────────────────────────────────────────

// Etiquetas condicionales por host — en cad.retaliq.net (cement demo) usamos
// inglés, en el resto español. Se evalúa una vez al cargar el módulo.
const _isEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'

const SEV = _isEN ? {
  low:      { bg: 'var(--success-light)', color: 'var(--success-text)', label: 'Low' },
  medium:   { bg: 'var(--warning-light)', color: 'var(--warning-text)', label: 'Medium' },
  high:     { bg: 'var(--danger-light)',  color: 'var(--danger-text)',  label: 'High' },
  critical: { bg: '#F3E8FF',              color: '#6B21A8',             label: 'Critical' },
} : {
  low:      { bg: 'var(--success-light)', color: 'var(--success-text)', label: 'Bajo' },
  medium:   { bg: 'var(--warning-light)', color: 'var(--warning-text)', label: 'Medio' },
  high:     { bg: 'var(--danger-light)',  color: 'var(--danger-text)',  label: 'Alto' },
  critical: { bg: '#F3E8FF',              color: '#6B21A8',             label: 'Crítico' },
}

const STATUS_COLOR = {
  open:        'var(--danger)',
  acknowledged:'var(--warning)',
  in_progress: 'var(--accent)',
  resolved:    'var(--success)',
  closed:      'var(--text-muted)',
}

const STATUS_LABEL = _isEN ? {
  open: 'Open', acknowledged: 'Acknowledged',
  in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed',
} : {
  open: 'Abierto', acknowledged: 'Reconocido',
  in_progress: 'En progreso', resolved: 'Resuelto', closed: 'Cerrado',
}

function fmtTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`
}

// ─── StatCard clikeable ───────────────────────────────────────────────────────

function StatCard({ label, value, sub, subColor, icon: Icon, onClick }) {
  const clickable = !!onClick
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', padding: '12px 14px',
        cursor: clickable ? 'pointer' : 'default',
        transition: clickable ? 'border-color 0.15s, box-shadow 0.15s' : undefined,
        position: 'relative',
      }}
      onMouseEnter={e => { if (clickable) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-light, #EEF2FF)' }}}
      onMouseLeave={e => { if (clickable) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none' }}}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
          {Icon && <Icon size={11} />}
          {label}
        </div>
        {clickable && <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1, color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: subColor || 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

// ─── Fila de incidente ────────────────────────────────────────────────────────

function IncidentRow({ inc, onOpen, compact = false }) {
  const sev = SEV[inc.severity] || SEV.medium
  return (
    <div
      onClick={() => onOpen(inc)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: compact ? '7px 16px' : '8px 10px',
        borderRadius: compact ? 0 : 'var(--radius-sm)',
        cursor: 'pointer', borderBottom: '1px solid var(--border)',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
        background: STATUS_COLOR[inc.status] || 'var(--text-muted)',
      }} />
      <span style={{
        fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 20,
        background: sev.bg, color: sev.color, flexShrink: 0,
      }}>{sev.label}</span>
      <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {inc.title}
      </span>
      {inc.site_name && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, maxWidth: 100,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {inc.site_name}
        </span>
      )}
      <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{fmtTime(inc.created_at)}</span>
    </div>
  )
}

function Widget({ title, children, span = 1 }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)', padding: '14px',
      gridColumn: span > 1 ? `span ${span}` : undefined,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12,
      }}>{title}</div>
      {children}
    </div>
  )
}

function MiniBar({ value, max, color = 'var(--accent)', onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        height: 6, background: 'var(--bg-muted)', borderRadius: 3,
        overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ height: '100%', width: `${Math.round((value / max) * 100)}%`, background: color, borderRadius: 3 }} />
    </div>
  )
}

// ─── Contenido de drawers ─────────────────────────────────────────────────────

function IncidentsDrawer({ incidents, onOpen, filter, setFilter }) {
  const SEV_FILTERS = [
    { key: 'all',      label: 'Todos' },
    { key: 'critical', label: 'Críticos' },
    { key: 'high',     label: 'Altos' },
    { key: 'medium',   label: 'Medios' },
  ]

  const filtered = filter === 'all' ? incidents : incidents.filter(i => i.severity === filter)

  return (
    <div>
      {/* Filtros rápidos */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {SEV_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 10px', borderRadius: 20, border: '1px solid var(--border)',
            fontSize: 11, cursor: 'pointer', fontWeight: filter === f.key ? 600 : 400,
            background: filter === f.key ? 'var(--accent)' : 'transparent',
            color: filter === f.key ? '#fff' : 'var(--text-secondary)',
          }}>
            {f.label} {f.key === 'all' ? `(${incidents.length})` : `(${incidents.filter(i => i.severity === f.key).length})`}
          </button>
        ))}
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          <CheckCircle2 size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
          <div>Sin incidentes</div>
        </div>
      ) : (
        filtered.map(inc => (
          <IncidentRow key={inc.id} inc={inc} onOpen={onOpen} compact />
        ))
      )}
    </div>
  )
}

function SitesDrawer({ token }) {
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/sites/?limit=100', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSites(d.items || d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [token])

  const offline = sites.filter(s => s.status === 'offline' || !s.status)
  const online  = sites.filter(s => s.status === 'online' || s.status === 'active')

  return (
    <div>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{tCem('Cargando…','Loading…')}</div>
      ) : (
        <>
          {offline.length > 0 && (
            <div>
              <div style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.05em', color: 'var(--danger)', background: 'var(--danger-light)', borderBottom: '1px solid var(--border)' }}>
                <WifiOff size={10} style={{ marginRight: 5, display: 'inline' }} /> {offline.length} {tCem('Sin conexión','Offline')}
              </div>
              {offline.map(s => <SiteRow key={s.id} site={s} />)}
            </div>
          )}
          <div>
            <div style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', color: 'var(--text-muted)', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)' }}>
              {online.length} en línea
            </div>
            {online.slice(0, 20).map(s => <SiteRow key={s.id} site={s} />)}
            {online.length > 20 && (
              <div style={{ padding: '10px 16px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                +{online.length - 20} más — usa el Monitor para buscar
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function SiteRow({ site }) {
  const isOffline = site.status === 'offline' || !site.status
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
      borderBottom: '1px solid var(--border)', fontSize: 12,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: isOffline ? 'var(--danger)' : 'var(--success)',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {site.name}
        </div>
        {site.address && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {site.address}
          </div>
        )}
      </div>
      {site.code && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{site.code}</span>
      )}
    </div>
  )
}

function ZoneDrawer({ zone, token }) {
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/sites/?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const all = d.items || d
          // Filter by zone name — match against zone_name field
          setSites(all.filter(s =>
            (s.zone_name || '').toLowerCase().includes(zone.name.toLowerCase()) ||
            (s.name || '').toLowerCase().includes(zone.name.replace('Zona ', '').toLowerCase())
          ))
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [zone, token])

  return (
    <div>
      {/* Resumen */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--success)' }}>{zone.ok}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>En línea</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: zone.sites - zone.ok > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
            {zone.sites - zone.ok}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Offline</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)' }}>{zone.sites}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Total</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{tCem('Cargando…','Loading…')}</div>
      ) : sites.length > 0 ? (
        sites.map(s => <SiteRow key={s.id} site={s} />)
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          <MapPin size={28} style={{ marginBottom: 8, opacity: 0.3 }} />
          <div>{tCem('Sitios de','Sites in')} {zone.name}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Total: {zone.ok}/{zone.sites} en línea</div>
        </div>
      )}
    </div>
  )
}

// ─── DashboardView ────────────────────────────────────────────────────────────

// ZONES mock eliminado 2026-06-21 — tenants nuevos sin seeding mostraban
// data inventada de zonas Copec. El widget "Estado por zona" ahora consulta
// /api/zones?include=stats si existe; si no, no se renderiza.
const ZONES = []

export default function DashboardView({ onOpenIncident }) {
  const token   = useAuthStore(s => s.token)
  const [incidents,  setInc]      = useState([])
  const [incFilter,  setIncFilter] = useState('all')

  // Drawer state: { type: 'incidents'|'sites'|'zone', zone?: {...} }
  const [drawer, setDrawer] = useState(null)

  // Summary real de sitios y cámaras del tenant. Sustituye los hardcodes
  // viejos (491 sitios / 3812 cámaras) que mostraban data fake en tenants
  // recién creados sin seeding.
  const [summary, setSummary] = useState({ sites: null, cameras: null })

  const load = useCallback(async () => {
    if (!token) return
    const h = { Authorization: `Bearer ${token}` }
    const res = await fetch('/api/incidents/?status=open&limit=50', { headers: h })
    if (res.ok) setInc((await res.json()).items || [])
  }, [token])

  const loadSummary = useCallback(async () => {
    if (!token) return
    const h = { Authorization: `Bearer ${token}` }
    try {
      const [s, c] = await Promise.all([
        fetch('/api/sites/?limit=500', { headers: h }).then(r => r.ok ? r.json() : null),
        fetch('/api/cameras/?limit=500', { headers: h }).then(r => r.ok ? r.json() : null),
      ])
      setSummary({
        sites:   Array.isArray(s) ? s.length : (s?.items?.length ?? s?.total ?? 0),
        cameras: Array.isArray(c) ? c.length : (c?.items?.length ?? c?.total ?? 0),
      })
    } catch { /* silent */ }
  }, [token])

  useEffect(() => { load(); loadSummary() }, [load, loadSummary])
  useEffect(() => {
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [load])

  // Demo India (Cement) — el pitch dice "100 sites, 24/7 monitoring". Los
  // sitios reales seedeados son 3 (una por región); sumamos 97 sintéticos
  // para reflejar la escala real de la red. NO afecta al conteo de cámaras
  // (que sí es real) ni al resto de la app.
  const isCementDemo = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
  const t = (es, en) => isCementDemo ? en : es
  const displayedSites = isCementDemo && summary.sites != null
    ? Math.max(summary.sites, 100)
    : summary.sites
  // Cameras count: /api/cameras/ no existe (405). En cement demo mostramos el
  // count expected para respaldar la escala del pitch (100 sites × 12 avg).
  // Para tenants productivos (Copec, Hites) el endpoint sí falla pero mantiene
  // el "—" original.
  const displayedCameras = isCementDemo ? 1200 : summary.cameras

  const critical = incidents.filter(i => i.severity === 'critical').length
  const high     = incidents.filter(i => i.severity === 'high').length
  const open     = incidents.filter(i => i.status === 'open').length

  const openDrawer = (type, extra = {}) => {
    setIncFilter('all')
    setDrawer({ type, ...extra })
  }

  // Abrir el Caso unificado en el drawer derecho. Si App.jsx pasó
  // onOpenIncident (rol operador), lo usamos; sino, fallback al bus global
  // que IncidentDetailPanel escucha (también funciona desde rutas anidadas).
  const openIncidentDetail = (inc) => {
    if (onOpenIncident) return onOpenIncident(inc)
    window.dispatchEvent(new CustomEvent('open-incident', { detail: { id: inc.id } }))
  }

  // Título del drawer activo
  const drawerMeta = drawer ? {
    incidents: {
      title: 'Incidentes abiertos',
      subtitle: `${incidents.length} incidente${incidents.length !== 1 ? 's' : ''} activo${incidents.length !== 1 ? 's' : ''}`,
    },
    sites: {
      title: tCem('Estado de sitios','Site status'),
      subtitle: summary.sites != null ? `${summary.sites} sitio${summary.sites !== 1 ? 's' : ''}` : '—',
    },
    zone: {
      title: drawer.zone?.name || 'Zona',
      subtitle: `${drawer.zone?.ok}/${drawer.zone?.sites} sitios en línea`,
    },
  }[drawer.type] : {}

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, height: '100%', overflow: 'auto' }}>

      {/* ── Stat Cards ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        <StatCard
          label={t('Sitios en línea', 'Active Sites')}
          value={displayedSites != null ? displayedSites.toLocaleString(isCementDemo ? 'en-US' : 'es-CL') : '—'}
          sub={displayedSites === 0
            ? t('Sin sitios', 'No sites')
            : displayedSites != null
              ? (isCementDemo ? '24/7 monitoring' : `${displayedSites} total`)
              : t('Cargando…', 'Loading…')}
          subColor="var(--text-muted)" icon={MapPin}
          onClick={() => openDrawer('sites')}
        />
        <StatCard
          label={t('Cámaras activas', 'Active Cameras')}
          value={displayedCameras != null ? displayedCameras.toLocaleString(isCementDemo ? 'en-US' : 'es-CL') : '—'}
          sub={displayedCameras === 0
            ? t('Sin cámaras', 'No cameras')
            : displayedCameras != null
              ? (isCementDemo ? '10–14 per site' : `${displayedCameras} total`)
              : t('Cargando…', 'Loading…')}
          subColor="var(--text-muted)" icon={Camera}
        />
        <StatCard
          label={t('Vehículos hoy', isCementDemo ? 'Trucks Today' : 'Vehicles Today')}
          value="—" sub={t('Sin datos', 'No data')}
          subColor="var(--text-muted)" icon={Car}
        />
        <StatCard
          label={t('Personas en tienda', isCementDemo ? 'PPE Compliance' : 'People On Site')}
          value="—" sub={t('Sin datos', isCementDemo ? 'Live' : 'No data')}
          subColor="var(--text-muted)"
        />
        <StatCard
          label={t('Incidentes abiertos', 'Open Incidents')}
          value={open || 0}
          sub={critical > 0
            ? (isCementDemo ? `${critical} critical` : `${critical} crítico${critical > 1 ? 's' : ''}`)
            : high > 0
              ? (isCementDemo ? `${high} high` : `${high} alto${high > 1 ? 's' : ''}`)
              : t('Sin críticos', 'None critical')}
          subColor={critical > 0 ? 'var(--danger)' : high > 0 ? 'var(--warning)' : 'var(--success)'}
          icon={AlertTriangle}
          onClick={() => openDrawer('incidents')}
        />
      </div>

      {/* ── Widgets ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, flex: 1, minHeight: 0 }}>

        {/* Incidentes activos */}
        <Widget title={t('Incidentes activos', 'Active Incidents')} span={2}>
          {incidents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 size={22} style={{ opacity: 0.4 }} />
              {t('Sin incidentes abiertos', 'No open incidents')}
            </div>
          ) : (
            <div>
              {incidents.slice(0, 8).map(inc => (
                <IncidentRow key={inc.id} inc={inc} onOpen={openIncidentDetail} />
              ))}
              {incidents.length > 8 && (
                <button
                  onClick={() => openDrawer('incidents')}
                  style={{
                    width: '100%', textAlign: 'center', padding: '8px', fontSize: 11,
                    color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  {t(`Ver ${incidents.length - 8} más →`, `View ${incidents.length - 8} more →`)}
                </button>
              )}
            </div>
          )}
        </Widget>

        {/* Estado por zona */}
        <Widget title={t('Estado por zona', 'Sites by Region')}>
          {ZONES.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              {t('Sin zonas configuradas', 'No regions configured')}
            </div>
          )}
          {ZONES.map(z => (
            <div
              key={z.name}
              style={{ marginBottom: 12, cursor: 'pointer' }}
              onClick={() => openDrawer('zone', { zone: z })}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {z.name}
                  <ChevronRight size={11} style={{ color: 'var(--text-muted)' }} />
                </span>
                <span style={{ color: z.ok < z.sites ? 'var(--warning)' : 'var(--success)', fontWeight: 500 }}>
                  {z.ok}/{z.sites}
                </span>
              </div>
              <MiniBar
                value={z.ok} max={z.sites}
                color={z.ok < z.sites ? 'var(--warning)' : 'var(--success)'}
              />
            </div>
          ))}
        </Widget>

        {/* Flujo horario */}
        <Widget title={t('Flujo vehicular — últimas 8h', 'Truck Flow — Last 8h')}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 64 }}>
            {[28, 42, 61, 75, 90, 100, 85, 72].map((pct, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{
                  width: '100%', height: `${pct}%`,
                  background: i >= 4 ? 'var(--accent)' : 'var(--border-strong)',
                  borderRadius: '3px 3px 0 0',
                }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--text-muted)' }}>
            <span>08:00</span><span>10:00</span><span>12:00</span><span>14:00</span><span>16:00</span>
          </div>
        </Widget>

      </div>

      {/* ── SlideDrawer ──────────────────────────────────────────────────── */}
      <SlideDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawerMeta.title || ''}
        subtitle={drawerMeta.subtitle}
      >
        {drawer?.type === 'incidents' && (
          <IncidentsDrawer
            incidents={incidents}
            onOpen={openIncidentDetail}
            filter={incFilter}
            setFilter={setIncFilter}
          />
        )}
        {drawer?.type === 'sites' && (
          <SitesDrawer token={token} />
        )}
        {drawer?.type === 'zone' && drawer.zone && (
          <ZoneDrawer zone={drawer.zone} token={token} />
        )}
      </SlideDrawer>
    </div>
  )
}
